import PropTypes from "prop-types"
import parse from "html-react-parser"
import {
  Link,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom"
import { episodePath } from "../shared/routes"
import {
  useWindowDimensions,
  BREAKPOINTS,
} from "source/shared/hooks/useWindowDimensions"
import {
  createContext,
  Suspense,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { sessionApiClient } from "@planningcenter/cc-api-client"
import { useDebounce } from "source/shared/hooks/useDebounce"
import { useUpdateEffect } from "source/shared/hooks/useUpdateEffect"
import { Alert, Button, Heading } from "@planningcenter/doxy-web"
import { Icon } from "source/shared/components"
import spinner from "source/svg/spinner.svg"
import { Loading } from "source/shared/components"
import {
  isPersisted,
  useEpisode,
  useMaybeDeletedEpisode,
  useEpisodeNote,
  useEpisodeNoteTemplate,
  useEpisodeNoteQueryKey,
  useNote,
  useNoteQueryKey,
} from "source/publishing/sermons/shared/queries"
import moment from "moment"
import {
  PENDO_EVENTS,
  trackPendoEvent,
} from "source/publishing/trackPendoEvent"
import { isEmpty } from "lodash/lang"
import useQueryString from "source/shared/hooks/useQueryString"
import { isChurchCenterApp } from "source/Layout"
import { useSession } from "source/shared/hooks/useSession"
import {
  Menu,
  MenuButton,
  MenuItem,
  MenuLink,
  MenuList,
} from "@reach/menu-button"
import { unlinkify } from "source/shared/linkify"
import useReactNativeBridgeComponent from "source/shared/hooks/useReactNativeBridgeComponent"
import { DELETED_EPISODE } from "source/publishing/sermons/shared/deleted_episode"
import {
  AlertDialog,
  AlertDialogLabel,
  AlertDialogDescription,
} from "@reach/alert-dialog"
import { Date } from "source/shared/DatetimeFmt"

const UNPERSISTED_NOTE_ID = "-1"

NotesFromNotesApi.propTypes = {
  noteId: PropTypes.string.isRequired,
  notesWrapperCss: PropTypes.object,
}
export function NotesFromNotesApi({ noteId, notesWrapperCss }) {
  const noteState = Notes.useNoteFromNoteApi({ noteId })
  return (
    <>
      <div
        className="mt-2 mt-0@sm mb-2@sm g-2 d-f"
        style={{
          maxWidth: "calc(100vw - 32px)",
          marginInline: "auto",
          alignItems: "center",
        }}
      >
        {noteState.episodeAttributes.library_art_url && (
          <div className="f-1">
            <img
              src={noteState.episodeAttributes.library_art_url}
              className="br-4p"
              alt="Episode thumbnail"
            />
          </div>
        )}
        <div className="d-f fd-c f-2">
          <h2 className="h2">{noteState.episodeAttributes.title}</h2>
          {noteState.episodeAttributes.published_to_library_at && (
            <p className="fs-4">
              <Date
                start={noteState.episodeAttributes.published_to_library_at}
                style="standard"
                year={true}
              />
            </p>
          )}
        </div>
      </div>
      <Notes
        deleteNotes={noteState.deleteNotes}
        episodeTitle={noteState.episodeAttributes.title}
        episodeId={noteState.episodeAttributes.id}
        initialContent={noteState.content}
        noteId={noteId}
        notesWrapperCss={notesWrapperCss}
        onChange={noteState.onChange}
        templateHtml={noteState.templateHtml}
      />
    </>
  )
}

export function NotesFromEpisodeApi({ episodeId, notesWrapperCss }) {
  const noteState = Notes.useNoteFromEpisodeApi({
    episodeId: episodeId,
  })
  const {
    meta: { authenticated },
  } = useSession()
  if (!authenticated) return <NotLoggedIn />
  return (
    <Notes
      deleteNotes={noteState.deleteNotes}
      draftMode={noteState.draftMode}
      episodeId={episodeId}
      episodeTitle={noteState.episodeTitle}
      initialContent={noteState.content}
      noteId={noteState.noteId}
      notesWrapperCss={notesWrapperCss}
      onChange={noteState.onChange}
      templateHtml={noteState.templateHtml}
    />
  )
}
NotesFromEpisodeApi.propTypes = {
  episodeId: PropTypes.string.isRequired,
  notesWrapperCss: PropTypes.object,
}

export function Notes({
  deleteNotes,
  draftMode = false,
  episodeId,
  episodeTitle,
  initialContent,
  noteId,
  notesWrapperCss,
  onChange: onChangeProp,
  templateHtml,
}) {
  const { reactifiedTemplate, blanks } = useMemo(
    () => Notes.reactifyTemplate(templateHtml),
    [templateHtml],
  )

  const [deleteWarningModalOpen, setDeleteWarningModalOpen] = useState(false)
  const [notes, setNotes] = useState(initialContent)
  const debouncedNotes = useDebounce(notes, 300)
  const onChange = (id, value) => setNotes((prev) => ({ ...prev, [id]: value }))

  useUpdateEffect(() => {
    onChangeProp(debouncedNotes)
  }, [debouncedNotes, onChangeProp])

  return (
    <div css={notesWrapperCss}>
      {draftMode && (
        <Alert
          text="Draft Mode: Your notes will not be saved."
          theme="warning"
        />
      )}
      <Notes.Context.Provider
        value={{
          blanks,
          episodeId,
          episodeTitle,
          noteId,
          onChange,
          templateHtml,
          values: notes,
        }}
      >
        <NotesHeader onDelete={() => setDeleteWarningModalOpen(true)} />
        <WarningModal
          open={deleteWarningModalOpen}
          setOpen={setDeleteWarningModalOpen}
          deleteNotes={deleteNotes}
        />
        <div css={styles.notesRoot}>{reactifiedTemplate}</div>
      </Notes.Context.Provider>
    </div>
  )
}
Notes.propTypes = {
  deleteNotes: PropTypes.func.isRequired,
  draftMode: PropTypes.bool,
  episodeId: PropTypes.string.isRequired,
  episodeTitle: PropTypes.string.isRequired,
  initialContent: PropTypes.object.isRequired,
  noteId: PropTypes.string.isRequired,
  notesWrapperCss: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  templateHtml: PropTypes.string.isRequired,
}
Notes.Suspense = function SuspenseNotes(props) {
  return <Suspense fallback={<Loading />}>{props.children}</Suspense>
}
Notes.Suspense.propTypes = {
  children: PropTypes.node.isRequired,
}

Notes.Context = createContext()
Notes.reactifyTemplate = function (templateHtml) {
  const blanks = {}
  const options = {
    transform(node, dom, index) {
      if (
        node.type === "span" &&
        node.props?.className === "fill-in-the-blank"
      ) {
        const id = node.props.id
        const templateValue = node.props.content || node.props.children
        blanks[id] = templateValue
        return (
          <NoteInput
            key={id}
            id={id}
            annotationId={`root-${index}`}
            templateValue={templateValue}
          />
        )
      }

      if (node.type === "textarea") {
        const id = node.props.id
        return <NoteAnnotation key={id} id={id} />
      }

      return node
    },
  }

  return { reactifiedTemplate: parse(templateHtml, options), blanks }
}
const useDraftMode = function (episodeId) {
  const { data: episode } = useEpisode(episodeId)

  const publishedAtInPast =
    episode.data.attributes.published_to_library_at &&
    moment(episode.data.attributes.published_to_library_at).isBefore(moment())
  const liveAtInPast =
    episode.data.attributes.published_live_at &&
    moment(episode.data.attributes.published_live_at).isBefore(moment())
  return !publishedAtInPast && !liveAtInPast
}
Notes.useNoteFromEpisodeApi = ({ episodeId }) => {
  const queryClient = useQueryClient()
  const noteQueryKey = useEpisodeNoteQueryKey(episodeId)
  const { data: episodeData } = useEpisode(episodeId)
  const { data: noteData } = useEpisodeNote(episodeId)
  const { data: noteTemplateHtml } = useEpisodeNoteTemplate(episodeId)
  const draftMode = useDraftMode(episodeId)
  const episodeTitle = episodeData.data.attributes.title
  const content = noteData?.content || {}
  const templateHtml = noteData?.template || noteTemplateHtml || ""
  const noteId = noteData?.id || UNPERSISTED_NOTE_ID

  const trackPendoEventCallback = useCallback(
    () => trackPendoEvent(PENDO_EVENTS.sermonNoteCreated),
    [],
  )
  const trackPendoEventOnce = useRunOnce(
    trackPendoEventCallback,
    !isEmpty(content),
  )

  const { mutate: noteMutate } = useMutation({
    mutationFn: (attributes) =>
      sessionApiClient.patch(`/publishing/v2/episodes/${episodeId}/note`, {
        data: { attributes },
      }),
    onSuccess: (newData) => {
      queryClient.setQueryData(noteQueryKey, newData)
    },
  })

  const navigate = useNavigate()
  const { mutate: deleteNotes } = useMutation({
    mutationFn: () => {
      return sessionApiClient.del(`/publishing/v2/episodes/${episodeId}/note`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: noteQueryKey,
      })

      navigate(episodePath(episodeId))
    },
  })

  const onChange = useCallback(
    (newNotes) => {
      if (draftMode) return

      const data = { content: newNotes, template: templateHtml }
      noteMutate(data)
      trackPendoEventOnce()
    },
    [draftMode, noteMutate, templateHtml, trackPendoEventOnce],
  )

  return {
    content,
    deleteNotes,
    draftMode,
    episodeTitle,
    noteId,
    onChange,
    templateHtml,
  }
}

Notes.useNoteFromNoteApi = ({ noteId }) => {
  const queryClient = useQueryClient()
  const noteQueryKey = useNoteQueryKey(noteId)
  const {
    data: { data: noteData, included },
  } = useNote(noteId)

  const content = noteData.attributes.content || {}
  const templateHtml = noteData.attributes.template || ""
  const episode = included.find(
    (i) =>
      i.type === "Episode" && i.id === noteData.relationships.episode.data?.id,
  )
  const episodeId =
    noteData.relationships.episode.data?.id ||
    `-${noteData.attributes.deleted_episode_attributes.id}`
  const episodeAttributes = {
    ...(episode?.attributes || noteData.attributes.deleted_episode_attributes),
    id: episodeId,
  }

  const { mutate: noteMutate } = useMutation({
    mutationFn: (attributes) => {
      return sessionApiClient.patch(
        `/publishing/v2/notes/${noteId}?include=episode`,
        {
          data: { attributes },
        },
      )
    },
    onSuccess: (newData) => {
      queryClient.setQueryData(noteQueryKey, newData)
    },
  })

  const navigate = useNavigate()
  const { mutate: deleteNotes } = useMutation({
    mutationFn: () => {
      return sessionApiClient.del(`/publishing/v2/notes/${noteId}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: noteQueryKey,
      })

      navigate("/me/notes")
    },
  })

  const onChange = useCallback(
    (content) => noteMutate({ content }),
    [noteMutate],
  )

  return {
    content,
    deleteNotes,
    episodeAttributes,
    onChange,
    templateHtml,
  }
}
function useRunOnce(func, alreadyDone = false) {
  const doneRef = useRef(alreadyDone)

  return useCallback(() => {
    if (doneRef.current) return

    doneRef.current = true
    func()
  }, [func])
}

function NoteAnnotation({ id }) {
  const { onChange: contextOnChange, values } = useContext(Notes.Context)
  const textareaRef = useRef(null)
  const onChange = (e) => {
    contextOnChange(id, e.target.value)
  }
  const value = values[id] || ""

  return (
    <>
      <div
        data-note-id={id}
        data-replicated-value={value}
        css={[
          styles.annotationWrapper,
          {
            ":after": [
              styles.growTextarea,
              {
                content: 'attr(data-replicated-value) " "',
                whiteSpace: "pre-wrap",
                visibility: "hidden",
              },
            ],
          },
        ]}
      >
        {value.length === 0 && (
          <span
            className="annotation-icon c-tint3 f-0 d-f jc-c ai-c"
            css={styles.annotationIcon}
          >
            <Icon symbol="general#plus" aria-hidden />
          </span>
        )}
        <textarea
          ref={textareaRef}
          placeholder="Add notes"
          value={value}
          onChange={onChange}
          css={[styles.growTextarea, styles.annotationTextarea]}
          rows={1}
        />
      </div>
    </>
  )
}
NoteAnnotation.displayName = "NoteAnnotation"
NoteAnnotation.propTypes = {
  id: PropTypes.string.isRequired,
}

function NoteInput({ id, templateValue = "" }) {
  const { blanks, onChange, values } = useContext(Notes.Context)
  const value = values[id] || ""
  const [isFocused, setIsFocused] = useState(false)
  const inputRef = useRef(null)

  const revealed =
    value.trim().toLowerCase() === (blanks[id] || "").trim().toLowerCase()
  const revealButtonVisible = !revealed

  const updateIfRevealed = () => {
    if (revealed) onChange(id, blanks[id])
  }

  return (
    <span
      className="fill-in-the-blank"
      data-current-value={value}
      data-template-value={templateValue}
      css={[
        styles.inputWrapper,
        {
          padding: revealButtonVisible ? "0 1.5rem 0 0" : "0",
          borderBottom: isFocused
            ? "1px solid var(--color-brand)"
            : "1px solid var(--color-tint3)",
          ":after": [
            styles.growInput,
            {
              content:
                isFocused || !value ? 'attr(data-template-value) " "' : '""',
              visibility: "hidden",
            },
          ],
          ":before": [
            styles.growInput,
            {
              content: 'attr(data-current-value) " "',
              marginRight: "-0.25rem",
              visibility: "hidden",
            },
          ],
        },
      ]}
    >
      <input
        ref={inputRef}
        onFocus={(_e) => {
          setIsFocused(true)
        }}
        onClick={(_e) => {
          setIsFocused(true)
        }}
        onBlur={() => {
          setIsFocused(false)
          updateIfRevealed()
        }}
        onChange={(e) => {
          onChange(id, e.target.value)
        }}
        value={value}
        css={styles.input}
      />
      <button
        className="btn naked-btn"
        css={{
          position: "absolute",
          right: 0,
          top: 0,
          bottom: 0,
          height: "auto",
          padding: "0 0.25rem",
          color: isFocused ? "var(--color-brand)" : "var(--color-tint3)",
          zIndex: 50,
          opacity: revealButtonVisible ? 1 : 0,
          visibility: revealButtonVisible ? "visible" : "hidden",
          transition: "opacity 0.2s ease-in-out",
          pointerEvents: revealButtonVisible ? "all" : "none",
        }}
        onTouchStart={(e) => {
          e.preventDefault()
        }}
        onMouseDown={(e) => {
          e.preventDefault()
        }}
        onClick={() => {
          onChange(id, blanks[id])
        }}
      >
        <Icon symbol={"general#show-eye"} aria-hidden />
      </button>
    </span>
  )
}
NoteInput.displayName = "NoteInput"
NoteInput.propTypes = {
  annotationId: PropTypes.string.isRequired,
  id: PropTypes.any.isRequired,
  templateValue: PropTypes.string,
}

function useIsLoggedIn() {
  const { data: currentPerson } = useSession()

  return Boolean(currentPerson.id)
}

ActionsMenu.propTypes = {
  buttonSize: PropTypes.string.isRequired,
  onDelete: PropTypes.func.isRequired,
}
function ActionsMenu({ buttonSize, onDelete }) {
  const {
    values: notes,
    templateHtml,
    episodeTitle,
    noteId,
    episodeId,
    blanks,
    onChange,
  } = useContext(Notes.Context)
  const [searchParams, _] = useQueryString()
  const canRNShare = isChurchCenterApp() && searchParams.supports_share
  const existingNote = isPersisted(noteId)
  const blankIds = Object.keys(blanks)
  const wrongBlanks = blankIds.filter((id) => notes[id] !== blanks[id])

  const { postMessage, supported: canBridgeShare } =
    useReactNativeBridgeComponent({
      component: "ShareSheet",
    })

  const canShare = canBridgeShare || canRNShare || navigator.canShare
  const canEmail = useIsLoggedIn()
  const [emailSent, setEmailSent] = useState(false)

  const fetchNoteAsText = useMutation({
    mutationFn: () =>
      sessionApiClient.post(`/publishing/v2/note_to_text`, {
        data: {
          attributes: {
            template: templateHtml,
            content: notes,
          },
        },
      }),
  })

  const share = () => {
    fetchNoteAsText.mutateAsync().then((data) => {
      const noteAsText = unlinkify(data.data.attributes.value)

      const title = `My Notes for ${episodeTitle}`

      if (canBridgeShare) {
        postMessage("share", {
          message: noteAsText,
          title,
        })
      } else if (canRNShare) {
        window.ReactNativeWebView.postMessage(
          JSON.stringify({
            type: "share",
            message: noteAsText,
            title,
          }),
        )
      } else {
        try {
          navigator.share({
            text: noteAsText,
            title,
          })
        } catch {
          alert("Error: unable to share notes.")
        }
      }
    })
  }

  const { mutate: sendViaEmail, isPending: isSendingEmail } = useMutation({
    mutationFn: () => {
      if (existingNote) {
        return sessionApiClient.post(
          `/publishing/v2/notes/${noteId}/export_to_email`,
        )
      } else {
        return sessionApiClient.post(
          `/publishing/v2/episodes/${episodeId}/note/export_to_email`,
        )
      }
    },
    onSuccess: () => {
      setEmailSent(true)
      setTimeout(() => setEmailSent(false), 1000)
    },
  })

  function getShareLabel(defaultLabel = "Send to my email") {
    switch (true) {
      case isSendingEmail:
        return (
          <div className="d-f g-1 ai-c">
            Sending <img src={spinner} alt="Sending" height={16} width={16} />
          </div>
        )
      case emailSent:
        return "Sent!"
      default:
        return defaultLabel
    }
  }

  return (
    <Menu>
      <MenuButton className={`btn ${buttonSize}-btn`}>
        {getShareLabel("Actions")}
        <span className="fs-6 pl-4p dropdown-trigger__icon">
          <Icon symbol="general#down-chevron" />
        </span>
      </MenuButton>

      <MenuList>
        {canEmail && (
          <MenuItem
            onSelect={sendViaEmail}
            disabled={isSendingEmail || emailSent}
          >
            {getShareLabel()}
          </MenuItem>
        )}
        {canShare && <MenuItem onSelect={share}>Export via...</MenuItem>}
        <MenuItem onSelect={onDelete}>Delete my note</MenuItem>
        {wrongBlanks.length > 0 && (
          <MenuItem
            onSelect={() => {
              wrongBlanks.forEach((id) => {
                onChange(id, blanks[id])
              })
            }}
          >
            Reveal all blanks
          </MenuItem>
        )}
        <MenuLink as={Link} to="/me/notes">
          View all my notes
        </MenuLink>
      </MenuList>
    </Menu>
  )
}

NotesHeader.propTypes = {
  onDelete: PropTypes.func.isRequired,
}
function NotesHeader({ onDelete }) {
  const location = useLocation()
  const { episodeId } = useContext(Notes.Context)
  const { width: winWidth } = useWindowDimensions()
  const buttonSize = winWidth < BREAKPOINTS.SM ? "md" : "sm"
  const noteScreen = location.pathname.includes("/notes")

  const { data: episodeData } = useMaybeDeletedEpisode(episodeId)

  const nav = useNavigate()
  const [searchParams] = useSearchParams()

  const existingEpisode = episodeData !== DELETED_EPISODE
  const activeEpisodeTime = episodeData?.included.find(
    (included) =>
      included.type === "EpisodeTime" &&
      included.attributes.current_state === "active",
  )

  function EpisodeLink() {
    const returnWithoutAddingToHistoryStack =
      searchParams.has("return_to_episode")

    if (returnWithoutAddingToHistoryStack) {
      return (
        <Button
          text="Go to episode"
          onClick={() => nav(-1)}
          size={buttonSize}
        />
      )
    } else {
      return (
        <Link
          to={episodePath(episodeId, {
            episodeTimeId: activeEpisodeTime?.id,
          })}
          className={`btn ${buttonSize}-btn primary-btn primary-btn`}
        >
          Go to episode
        </Link>
      )
    }
  }

  return (
    <div
      className="notes-header"
      css={[styles.notesHeader, styles.notesHeaderSticky]}
    >
      <h2>Notes</h2>
      <div className="d-f g-1 ai-c jc-fe f-1">
        <ActionsMenu buttonSize={buttonSize} onDelete={onDelete} />
        {noteScreen && existingEpisode && <EpisodeLink />}
      </div>
    </div>
  )
}

function NotLoggedIn() {
  return (
    <div className="p-2 d-f ai-c jc-c fd-c g-1 h-100%">
      <Heading level={2} size={3} text="Log in to take notes" />
      <a
        href={`/login?return=${window.location.href}`}
        className="btn compact-btn secondary-btn"
      >
        Log in
      </a>
    </div>
  )
}

const styles = {
  notesRoot: {
    padding: "1rem",
    paddingBottom: "64px",
    lineHeight: 1.5,
    "ul,ol": {
      paddingLeft: "1rem",
      li: {
        paddingLeft: 0,
      },
    },
    "li > div": {
      marginBottom: 0,
    },
    fontSize: "18px",
    blockquote: {
      borderLeft: "1px solid var(--color-tint4)",
      paddingLeft: "1rem",
      margin: 0,
      fontSize: "16px",
      fontStyle: "italic",
      color: "var(--color-tint2)",
    },
    "> *, li": {
      marginBottom: "1rem",
    },
  },
  annotationIcon: {
    fontSize: 12,
    position: "absolute",
    top: 0,
    left: 0,
    width: "12px",
    height: "32px",
  },
  annotationWrapper: {
    marginTop: "4px",
    marginBottom: "16px",
    minHeight: 0,
    borderRadius: 4,
    position: "relative",
    overflow: "hidden",
    display: "grid",
    flex: "1",
    ":has(textarea:focus)": {
      background: "transparent",
    },
    ":has(textarea:focus) .annotation-icon": {
      display: "none",
    },
  },
  annotationTextarea: {
    resize: "none",
    overflow: "hidden",
    borderRadius: 4,
    backgroundColor: "transparent",
    border: "1px solid transparent",
    "&::placeholder": {
      color: "var(--color-tint3)",
      lineHeight: "1",
      paddingLeft: "12px",
    },
    "&:focus::placeholder": {
      visibility: "hidden",
    },
    "&:not(:empty)": {
      borderColor: "var(--color-tint5)",
    },
    "&:focus": {
      borderColor: "var(--color-brand)",
    },
  },
  inputWrapper: {
    paddingBottom: "0.25rem",
    fontSize: "inherit",
    fontWeight: "inherit",
    position: "relative",
    lineHeight: "1",
    overflow: "hidden",
    display: "inline-grid",
    flex: "1",
    gridArea: "1 / 1 / 2 / 2",
    whiteSpace: "pre-wrap",
    transition: "padding 0.2s ease-in-out",
  },
  input: {
    position: "absolute",
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    opacity: 1,
    border: "none",
    backgroundColor: "transparent",
    fontSize: "inherit",
    fontWeight: "inherit",
    padding: "0",
    transition: "opacity 0.3s ease-in-out 0.1s",
    outline: "none",
    color: "inherit",
    paddingBottom: "0.25rem",
  },
  growInput: {
    paddingBottom: "0.25rem",
    border: "none",
    font: "inherit",
    fontSize: "inherit",
    fontWeight: "inherit",
    lineHeight: "inherit",
    color: "var(--color-tint1)",
    gridArea: "1 / 1 / 2 / 2",
    outlineColor: "transparent !important",
    boxShadow: "none !important",
    whiteSpace: "pre-wrap",
  },
  growTextarea: {
    border: "none",
    padding: "0.5rem",
    font: "inherit",
    fontSize: "16px",
    color: "var(--color-tint1)",
    gridArea: "1 / 1 / 2 / 2",
    outlineColor: "transparent !important",
    boxShadow: "none !important",
    lineHeight: 1.5,
    whiteSpace: "pre-wrap",
  },
  notesHeader: {
    ".btn": {
      background: "var(--color-tint6)",
      color: "var(--color-tint1) !important",
    },
  },
  notesHeaderSticky: {
    background: "var(--color-tint10)",
    borderBottom: "1px solid var(--color-tint6)",
    borderRadius: "4px 4px 0 0",
    display: "flex",
    gap: "0.5rem",
    paddingInline: "1rem",
    paddingBlock: "0.5rem",
    position: "sticky",
    top: "0",
    zIndex: 900,
    ":not(.live-episode-notes-panel) &": {
      [`@media (min-width: ${BREAKPOINTS.LG}px)`]: {
        top: "68px",
      },
    },
    ".live-episode-notes-panel &": {
      borderBottom: "1px solid var(--color-tint8)",
      h2: {
        display: "none",
      },
    },
  },
  notesHeaderBelow: {
    display: "flex",
    gap: "0.5rem",
    paddingInline: "1rem",
    paddingBlock: "1rem",
  },
}

WarningModal.propTypes = {
  deleteNotes: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired,
}

function WarningModal({ open, setOpen, deleteNotes }) {
  if (!open) return null

  return (
    <AlertDialog leastDestructiveRef={1} onDismiss={close}>
      <AlertDialogLabel>Delete note</AlertDialogLabel>
      <AlertDialogDescription>
        Are you sure? This action can&rsquo;t be undone.
        <div className="d-f jc-fe mt-3 g-2">
          <div className="d-f jc-fe mt-3">
            <button onClick={() => setOpen(false)} className="btn text-btn">
              Cancel
            </button>
          </div>
          <div className="d-f jc-fe mt-3">
            <button
              onClick={() => {
                setOpen(false)
                deleteNotes()
              }}
              className="btn destroy-btn"
            >
              Delete
            </button>
          </div>
        </div>
      </AlertDialogDescription>
    </AlertDialog>
  )
}
